import React, { useMemo } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
} from "@material-ui/core";

import { parseTemplate } from "../../WabaTemplateModal/helpers";
import WabaTemplatePreview from "../../WabaTemplateModal/WabaTemplatePreview";
import useCampaignTemplates from "../hooks/useCampaignTemplates";

// Columnas de ContactListItem disponibles para el mapeo de variables (0369 HC5).
const COLUMN_OPTIONS = [
  { value: "name", label: "Nombre" },
  { value: "email", label: "Email" },
  { value: "colum4", label: "Columna 4" },
  { value: "colum5", label: "Columna 5" },
  { value: "colum6", label: "Columna 6" },
  { value: "colum7", label: "Columna 7" },
  { value: "colum8", label: "Columna 8" },
  { value: "colum9", label: "Columna 9" },
  { value: "colum10", label: "Columna 10" },
];

// Un mapeo de variable: columna del contacto o valor fijo.
function VarMapper({ label, item, onChange, disabled }) {
  const type = item?.type || "column";
  return (
    <Grid container spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>{label}</Typography>
      </Grid>
      <Grid item xs={6} sm={4}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel>Origen</InputLabel>
          <Select
            label="Origen"
            value={type}
            disabled={disabled}
            onChange={(e) =>
              onChange({ type: e.target.value, value: e.target.value === "fixed" ? "" : "name" })
            }
          >
            <MenuItem value="column">Columna del contacto</MenuItem>
            <MenuItem value="fixed">Valor fijo</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={4}>
        {type === "column" ? (
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Columna</InputLabel>
            <Select
              label="Columna"
              value={item?.value || "name"}
              disabled={disabled}
              onChange={(e) => onChange({ type: "column", value: e.target.value })}
            >
              {COLUMN_OPTIONS.map((c) => (
                <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth size="small" variant="outlined" label="Texto"
            value={item?.value || ""}
            disabled={disabled}
            onChange={(e) => onChange({ type: "fixed", value: e.target.value })}
          />
        )}
      </Grid>
    </Grid>
  );
}

export default function CampaignTemplateSection({ values, setFieldValue, whatsappId, disabled }) {
  const { templates } = useCampaignTemplates(whatsappId);

  const selected = useMemo(
    () => templates.find((t) => String(t.id) === String(values.templateId)),
    [templates, values.templateId]
  );
  const parsed = useMemo(() => (selected ? parseTemplate(selected.components) : null), [selected]);

  const mapping = values.templateVariables || {};
  const headerVars = parsed?.header && !parsed.header.isMedia ? parsed.header.varCount : 0;
  const bodyVars = parsed?.body?.varCount || 0;

  const setMapping = (patch) => setFieldValue("templateVariables", { ...mapping, ...patch });

  const setVarItem = (which, i, patch) => {
    const arr = [...(mapping[which] || [])];
    arr[i] = { ...(arr[i] || { type: "column", value: "name" }), ...patch };
    setMapping({ [which]: arr });
  };

  const handleSelectTemplate = (id) => {
    setFieldValue("templateId", id);
    const tpl = templates.find((t) => String(t.id) === String(id));
    const p = tpl ? parseTemplate(tpl.components) : null;
    const hVars = p?.header && !p.header.isMedia ? p.header.varCount : 0;
    const bVars = p?.body?.varCount || 0;
    setFieldValue("templateVariables", {
      headerImageUrl: "",
      header: Array.from({ length: hVars }, () => ({ type: "column", value: "name" })),
      body: Array.from({ length: bVars }, () => ({ type: "column", value: "name" })),
    });
  };

  // Preview: sustituye las variables por etiquetas legibles del mapeo.
  const labelFor = (item) => {
    if (!item) return "";
    if (item.type === "fixed") return item.value || "";
    const col = COLUMN_OPTIONS.find((c) => c.value === item.value);
    return col ? `[${col.label}]` : `[${item.value}]`;
  };

  return (
    <Grid xs={12} item>
      <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
        <InputLabel>Plantilla aprobada</InputLabel>
        <Select
          label="Plantilla aprobada"
          value={values.templateId || ""}
          disabled={disabled}
          onChange={(e) => handleSelectTemplate(e.target.value)}
        >
          {templates.length === 0 && (
            <MenuItem value="" disabled>
              No hay plantillas aprobadas en esta conexión
            </MenuItem>
          )}
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name} ({t.language})</MenuItem>
          ))}
        </Select>
      </FormControl>

      {parsed && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            {parsed.header?.isMedia && (
              <TextField
                fullWidth size="small" variant="outlined"
                label="URL de la imagen del encabezado (misma para toda la campaña)"
                placeholder="https://..."
                value={mapping.headerImageUrl || ""}
                disabled={disabled}
                onChange={(e) => setMapping({ headerImageUrl: e.target.value })}
                style={{ marginBottom: 12 }}
              />
            )}

            {headerVars > 0 && (
              <>
                <Typography variant="caption" color="textSecondary">Variables del encabezado</Typography>
                {Array.from({ length: headerVars }).map((_, i) => (
                  <VarMapper
                    key={`h-${i}`}
                    label={`Encabezado {{${parsed.header.varKeys?.[i] ?? i + 1}}}`}
                    item={mapping.header?.[i]}
                    disabled={disabled}
                    onChange={(patch) => setVarItem("header", i, patch)}
                  />
                ))}
              </>
            )}

            {bodyVars > 0 && (
              <>
                <Typography variant="caption" color="textSecondary">Variables del cuerpo</Typography>
                {Array.from({ length: bodyVars }).map((_, i) => (
                  <VarMapper
                    key={`b-${i}`}
                    label={`Cuerpo {{${parsed.body.varKeys?.[i] ?? i + 1}}}`}
                    item={mapping.body?.[i]}
                    disabled={disabled}
                    onChange={(patch) => setVarItem("body", i, patch)}
                  />
                ))}
              </>
            )}

            {headerVars === 0 && bodyVars === 0 && !parsed.header?.isMedia && (
              <Typography variant="body2" color="textSecondary">
                Esta plantilla no tiene variables. Se enviará igual a todos los contactos.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6 }}>
              Vista previa (con etiquetas del mapeo)
            </Typography>
            <WabaTemplatePreview
              parsed={parsed}
              headerMediaUrl={mapping.headerImageUrl}
              headerTextVars={(mapping.header || []).map(labelFor)}
              bodyVars={(mapping.body || []).map(labelFor)}
            />
          </Grid>
        </Grid>
      )}

      {!parsed && (
        <Box mt={1}>
          <Typography variant="body2" color="textSecondary">
            Selecciona una plantilla aprobada para configurar sus variables.
          </Typography>
        </Box>
      )}
    </Grid>
  );
}
